/**
 * Pages newsletter — Phase 2A étendue L15c.
 *
 * Exporte :
 *   - NewsletterListPage    : liste paginée filtrable
 *   - NewsletterEditorPage  : éditeur d'articles (réorganisation ▲▼, preview live)
 *   - NewsletterDetailPage  : preview complet + envoi + log envois
 *
 * Refonte R7.2 (mai 2026) :
 *   - Topbar glass cohérent avec halo grc + sub-nav pills glass (cohérent R7.1)
 *   - Layout 2 colonnes (éditeur articles + preview live) en Cards glass
 *   - ArticleModal en bg-ink/60 backdrop-blur-md
 *   - Preview WYSIWYG : header grc en bandeau + articles dans bg-surface/40
 *   - Stats envoi avec progress bar grc + dl key-value glass
 *
 * Logique métier 100% préservée :
 *   - api.grc.newsletters.list/get/create/update/send/cancel/delete
 *   - api.grc.newsletters.articles.create/update/delete/reorder
 *   - Mini-markdown function md() inchangée (gras/italique/lien/listes/paragraphes)
 *   - canCreate (super_admin/admin/manager), canEdit, canDelete (super_admin/admin)
 *   - Auto-save on-blur pour titre/sujetEmail/intro/conclusion (mode éditeur)
 *   - readonly si statut !== 'brouillon'
 *   - Move ▲▼ avec reorder API
 *   - confirmSend 2-step + polling reload après envoi (1.5s puis 5s)
 *   - Statut workflow : brouillon → envoyee/annulee, suppression admin
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';     // R13 — remplace window.confirm
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { SelecteurAlerte } from '@/components/grc/SelecteurAlerte';
import {
  ArrowLeft, Plus, Send, Save, Trash2, X, Eye, EyeOff,
  PenLine, Newspaper, CalendarDays, HardHat, Users as UsersIcon,
  GraduationCap, FileText, MailOpen, AlertCircle, CheckCircle2,
  ChevronUp, ChevronDown, AlertTriangle, FileDown,
} from 'lucide-react';
import type {
  GrcNewsletterDetail, GrcNewsletterArticle, GrcNewsletterStatut, GrcArticleCategorie,
} from '@roronoa-collectivites/shared-types';

const CAT_INFO: Record<GrcArticleCategorie, { label: string; Icon: typeof PenLine }> = {
  edito:      { label: 'Édito du maire', Icon: PenLine },
  actualite:  { label: 'Actualité',      Icon: Newspaper },
  evenement:  { label: 'Événement',      Icon: CalendarDays },
  travaux:    { label: 'Travaux',        Icon: HardHat },
  associatif: { label: 'Associatif',     Icon: UsersIcon },
  scolaire:   { label: 'Scolaire',       Icon: GraduationCap },
  divers:     { label: 'Divers',         Icon: FileText },
};

const STATUT_BADGE: Record<GrcNewsletterStatut, { label: string; variant: 'neutral' | 'warning' | 'success' | 'urgent' }> = {
  brouillon:  { label: 'Brouillon',  variant: 'neutral' },
  programmee: { label: 'Programmée', variant: 'warning' },
  en_envoi:   { label: 'En envoi',   variant: 'warning' },
  envoyee:    { label: 'Envoyée',    variant: 'success' },
  annulee:    { label: 'Annulée',    variant: 'neutral' },
};

const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong disabled:opacity-60 disabled:cursor-not-allowed';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong disabled:opacity-60 disabled:cursor-not-allowed';

// =============================================================================
// LAYOUT commun
// =============================================================================

function NewsLayout({
  title, eyebrow = 'GRC & communication', icon = MailOpen, children, action,
}: {
  title: string;
  eyebrow?: string;
  icon?: typeof MailOpen;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <GrcLayout active="communication">
      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête de page */}
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={icon} accent="grc" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">{eyebrow}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {title}
            </h1>
          </div>
          {action && <div className="ml-auto">{action}</div>}
        </div>

        {children}
      </main>
    </GrcLayout>
  );
}

// Mini-markdown (sync avec backend) — INCHANGÉ
function md(text: string | null | undefined): string {
  if (!text) return '';
  let s = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" style="color:#1D9E75">$1</a>');
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  s = s.replace(/(?:^|\n)(- [^\n]+(?:\n- [^\n]+)*)/g, (_m, list: string) => {
    const items = list.trim().split(/\n/).map((l: string) => `<li>${l.replace(/^- /, '')}</li>`).join('');
    return `\n<ul style="margin:0.5em 0;padding-left:1.5em">${items}</ul>`;
  });
  s = s.split(/\n\n+/).map((p) => {
    if (p.startsWith('<ul') || p.startsWith('<h')) return p;
    return `<p style="margin:0.5em 0">${p.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');
  return s;
}

// =============================================================================
// LISTE
// =============================================================================

export function NewsletterListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.grc.newsletters.list>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatut, setFilterStatut] = useState<string>('all');
  const [showCreate, setShowCreate] = useState(false);

  const canCreate = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  async function reload() {
    setLoading(true); setError(null);
    try {
      setData(await api.grc.newsletters.list({
        statut: filterStatut === 'all' ? undefined : filterStatut,
      }));
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally { setLoading(false); }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [filterStatut]);

  return (
    <NewsLayout
      title="Newsletters municipales"
      icon={MailOpen}
      action={canCreate ? (
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Nouvelle édition</span>
        </Button>
      ) : undefined}
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {(['all', 'brouillon', 'envoyee', 'annulee'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatut(s)}
              className={[
                'inline-flex h-8 items-center justify-center rounded-r-pill border px-3 text-xs font-medium transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                filterStatut === s
                  ? 'bg-ink text-bg border-ink shadow-soft'
                  : 'border-border bg-surface/40 backdrop-blur-glass text-ink-muted hover:bg-surface/70 hover:text-ink',
              ].join(' ')}
            >
              {s === 'all' ? 'Toutes' : s === 'brouillon' ? 'Brouillons' : s === 'envoyee' ? 'Envoyées' : 'Annulées'}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-grc border-t-transparent"
              role="status"
              aria-label="Chargement"
            />
            <p className="mt-4 text-sm text-ink-muted">Chargement des newsletters…</p>
          </div>
        </Card>
      ) : !data || data.items.length === 0 ? (
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div className="flex justify-center mb-3">
              <LogoMark icon={MailOpen} accent="grc" size="lg" />
            </div>
            <p className="text-sm text-ink-muted">Aucune newsletter</p>
            {canCreate && (
              <Button variant="primary" size="sm" className="mt-4" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Créer la première
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((n) => {
            const st = STATUT_BADGE[n.statut];
            const taux = n.envoyeCount > 0 ? Math.round((n.ouvertCount / n.envoyeCount) * 100) : null;
            return (
              <Link
                key={n.id}
                to={`/grc/newsletters/${n.id}`}
                className="group block w-full rounded-r-xl border border-border backdrop-blur-glass px-4 py-3 shadow-soft transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:border-grc/30 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                style={{
                  backgroundImage: 'linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-grc/15 text-grc-fg flex-shrink-0"
                    aria-hidden="true"
                  >
                    <MailOpen className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono text-2xs text-ink-muted">N°{n.numero}</span>
                      <Badge variant={st.variant} size="sm">{st.label}</Badge>
                      {n.statut === 'envoyee' && n.dateEnvoiReel && (
                        <span className="text-2xs text-ink-muted">
                          envoyée le {new Date(n.dateEnvoiReel).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate font-medium text-ink">{n.titre}</p>
                    <p className="truncate text-xs text-ink-muted">
                      {n.statut === 'envoyee'
                        ? <>{n.envoyeCount} envois{taux !== null && <span className="text-grc-fg"> · {taux}% d'ouverture</span>}</>
                        : n.sujetEmail}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); navigate(`/grc/newsletters/${id}/edit`); }}
        />
      )}
    </NewsLayout>
  );
}

function CreateModal({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [titre, setTitre] = useState('');
  const [sujetEmail, setSujetEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const r = await api.grc.newsletters.create({ titre: titre.trim(), sujetEmail: sujetEmail.trim() });
      onCreated(r.id);
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-md p-4"
        >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              Nouvelle newsletter
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Titre de l'édition *" htmlFor="nl-titre">
              <input
                id="nl-titre"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                required minLength={3} maxLength={200}
                placeholder="Ex : Lettre municipale Mai 2026"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Objet de l'email *" htmlFor="nl-sujet">
              <input
                id="nl-sujet"
                value={sujetEmail}
                onChange={(e) => setSujetEmail(e.target.value)}
                required minLength={3} maxLength={250}
                placeholder="Votre lettre municipale de mai"
                className={INPUT_CLS}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                {submitting ? 'Création…' : 'Créer le brouillon'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// ÉDITEUR
// =============================================================================

export function NewsletterEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm, dialogElement } = useConfirmDialog();   // R13
  const [data, setData] = useState<GrcNewsletterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<GrcNewsletterArticle | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);

  async function reload() {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      setData(await api.grc.newsletters.get(id));
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setLoading(false); }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  async function saveMeta(updates: Record<string, unknown>) {
    if (!data) return;
    try {
      await api.grc.newsletters.update(data.id, updates);
      setSuccess('Modifications enregistrées');
      setTimeout(() => setSuccess(null), 2000);
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur sauvegarde');
    }
  }
  async function delArticle(aid: string) {
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Supprimer cet article ?',
      description: 'L\'article ne sera plus inclus dans la newsletter.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    try { await api.grc.newsletters.articles.delete(aid); await reload(); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }
  async function move(aid: string, dir: 'up' | 'down') {
    if (!data) return;
    const idx = data.articles.findIndex((a) => a.id === aid);
    if (idx === -1) return;
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= data.articles.length) return;
    const arr = [...data.articles];
    [arr[idx], arr[newIdx]] = [arr[newIdx]!, arr[idx]!];
    try {
      await api.grc.newsletters.articles.reorder(data.id, arr.map((a) => a.id));
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur réorganisation');
    }
  }

  if (loading) return (
    <NewsLayout title="Édition newsletter" icon={PenLine}>
      <Card variant="glass" padded>
        <div className="py-8 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent" role="status" />
          <p className="mt-4 text-sm text-ink-muted">Chargement de l'éditeur…</p>
        </div>
      </Card>
    </NewsLayout>
  );
  if (error || !data) return (
    <NewsLayout title="Édition newsletter" icon={PenLine}>
      <div className="space-y-3">
        <Alert variant="error" icon={AlertCircle}>{error ?? 'Newsletter introuvable'}</Alert>
        <Button variant="secondary" size="sm" onClick={() => navigate('/grc/newsletters')}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
        </Button>
      </div>
    </NewsLayout>
  );

  const readonly = data.statut !== 'brouillon';

  return (
    <NewsLayout
      title={`Éditer N°${data.numero}`}
      icon={PenLine}
      action={
        <div className="flex items-center gap-2">
          <Link
            to="/grc/newsletters"
            className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Retour
          </Link>
          <Link to={`/grc/newsletters/${data.id}`}>
            <Button type="button" variant="primary" size="sm">
              Aperçu / Envoyer →
            </Button>
          </Link>
        </div>
      }
    >
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-3">{success}</Alert>
      )}
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>
      )}
      {readonly && (
        <Alert variant="warning" icon={AlertTriangle} className="mb-3">
          Cette newsletter est en <strong>{data.statut}</strong> et n'est plus modifiable.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          {/* Métadonnées */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Métadonnées</SectionEyebrow>
            <div className="space-y-3">
              <Field label="Titre" htmlFor="ed-titre">
                <input
                  id="ed-titre"
                  defaultValue={data.titre}
                  disabled={readonly}
                  onBlur={(e) => { if (e.target.value !== data.titre) void saveMeta({ titre: e.target.value }); }}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Objet de l'email" htmlFor="ed-sujet">
                <input
                  id="ed-sujet"
                  defaultValue={data.sujetEmail ?? undefined}
                  disabled={readonly}
                  onBlur={(e) => { if (e.target.value !== data.sujetEmail) void saveMeta({ sujetEmail: e.target.value }); }}
                  className={INPUT_CLS}
                />
              </Field>
              {/* Mig 234 — une newsletter émise pendant un événement doit
                  apparaître dans le journal de l'incident. */}
              <SelecteurAlerte
                id="ed-alerte"
                value={data.alerteId ?? ''}
                onChange={(v) => { void saveMeta({ alerteId: v || null }); }}
              />

              <Field label="Introduction (markdown)" htmlFor="ed-intro">
                <textarea
                  id="ed-intro"
                  defaultValue={data.intro ?? ''}
                  disabled={readonly}
                  rows={3} maxLength={10000}
                  placeholder="Mot du maire, présentation de cette édition…"
                  onBlur={(e) => { if (e.target.value !== (data.intro ?? '')) void saveMeta({ intro: e.target.value || null }); }}
                  className={TEXTAREA_CLS}
                />
              </Field>
              <Field label="Conclusion (markdown)" htmlFor="ed-conclu">
                <textarea
                  id="ed-conclu"
                  defaultValue={data.conclusion ?? ''}
                  disabled={readonly}
                  rows={2} maxLength={10000}
                  placeholder="Signature, contacts utiles…"
                  onBlur={(e) => { if (e.target.value !== (data.conclusion ?? '')) void saveMeta({ conclusion: e.target.value || null }); }}
                  className={TEXTAREA_CLS}
                />
              </Field>
            </div>
          </Card>

          {/* Articles */}
          <Card variant="glass" padded>
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <SectionEyebrow as="h2">Articles ({data.articles.length})</SectionEyebrow>
              {!readonly && (
                <Button type="button" variant="primary" size="sm" onClick={() => setShowAdd(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
                </Button>
              )}
            </div>

            {data.articles.length === 0 ? (
              <div className="rounded-r-lg border border-dashed border-border bg-surface/30 backdrop-blur-glass px-4 py-8 text-center">
                <FileText className="mx-auto mb-2 h-8 w-8 text-ink-dim" aria-hidden="true" />
                <p className="text-sm text-ink-muted">Aucun article dans cette newsletter</p>
                {!readonly && (
                  <Button type="button" variant="primary" size="sm" className="mt-3" onClick={() => setShowAdd(true)}>
                    Ajouter le premier article
                  </Button>
                )}
              </div>
            ) : (
              <ul className="space-y-2">
                {data.articles.map((a, idx) => {
                  const cat = a.categorie ? CAT_INFO[a.categorie] : null;
                  return (
                    <li
                      key={a.id}
                      className="flex items-start gap-2 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3"
                    >
                      {!readonly && (
                        <div className="flex flex-col gap-0.5 pt-1">
                          <button
                            type="button"
                            onClick={() => void move(a.id, 'up')}
                            disabled={idx === 0}
                            className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            aria-label="Monter"
                          >
                            <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void move(a.id, 'down')}
                            disabled={idx === data.articles.length - 1}
                            className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            aria-label="Descendre"
                          >
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          {cat && (
                            <span className="inline-flex items-center gap-1 text-2xs uppercase tracking-r-eyebrow text-ink-muted">
                              <cat.Icon className="h-3 w-3" aria-hidden="true" /> {cat.label}
                            </span>
                          )}
                          <span className="text-2xs text-ink-dim tabular-nums">#{idx + 1}</span>
                        </div>
                        <p className="text-sm font-semibold text-ink">{a.titre}</p>
                        <p className="line-clamp-2 text-xs text-ink-muted">{a.contenu}</p>
                        {a.imageUrl && (
                          <p className="mt-1 text-2xs text-ink-dim">📷 {a.imageAlt || 'image'}</p>
                        )}
                      </div>
                      {!readonly && (
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(a)}
                            className="rounded-r-md p-1.5 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            aria-label="Modifier"
                          >
                            <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void delArticle(a.id)}
                            className="rounded-r-md p-1.5 text-urgent transition-colors hover:bg-urgent/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        {/* Preview live */}
        <aside className="space-y-3">
          <Card variant="glass" padded>
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <SectionEyebrow as="h3">Aperçu</SectionEyebrow>
              <button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                className="inline-flex items-center gap-1 rounded-r-md px-1.5 py-0.5 text-2xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                {previewMode ? (
                  <><EyeOff className="h-3 w-3" aria-hidden="true" /> Masquer</>
                ) : (
                  <><Eye className="h-3 w-3" aria-hidden="true" /> Afficher</>
                )}
              </button>
            </div>
            {previewMode ? (
              <PreviewMini data={data} />
            ) : (
              <div className="rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3 text-center">
                <Eye className="mx-auto mb-1 h-6 w-6 text-ink-dim" aria-hidden="true" />
                <p className="text-xs text-ink-muted">Cliquer pour afficher</p>
              </div>
            )}
          </Card>
          {!readonly && (
            <Card variant="glass" padded>
              <p className="text-xs text-ink-muted">
                <strong className="text-ink">Auto-save :</strong> les modifications sont enregistrées
                à la perte de focus (champ titre, intro, conclusion).
              </p>
            </Card>
          )}
        </aside>
      </div>

      {showAdd && (
        <ArticleModal
          onClose={() => setShowAdd(false)}
          onSave={async (input) => {
            await api.grc.newsletters.articles.create(data.id, input);
            setShowAdd(false); await reload();
          }}
        />
      )}
      {editing && (
        <ArticleModal
          article={editing}
          onClose={() => setEditing(null)}
          onSave={async (input) => {
            await api.grc.newsletters.articles.update(editing.id, input);
            setEditing(null); await reload();
          }}
        />
      )}
      {dialogElement}{/* R13 — Dialog R2 (confirmation suppression article) */}
    </NewsLayout>
  );
}

function ArticleModal({
  article, onClose, onSave,
}: {
  article?: GrcNewsletterArticle;
  onClose: () => void;
  onSave: (input: {
    categorie?: string; titre: string; contenu: string;
    imageUrl?: string; imageAlt?: string; lienUrl?: string;
  }) => Promise<void>;
}) {
  const [categorie, setCategorie] = useState<string>(article?.categorie ?? 'actualite');
  const [titre, setTitre] = useState(article?.titre ?? '');
  const [contenu, setContenu] = useState(article?.contenu ?? '');
  const [imageUrl, setImageUrl] = useState(article?.imageUrl ?? '');
  const [imageAlt, setImageAlt] = useState(article?.imageAlt ?? '');
  const [lienUrl, setLienUrl] = useState(article?.lienUrl ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await onSave({
        categorie: categorie || undefined,
        titre: titre.trim(), contenu: contenu.trim(),
        imageUrl: imageUrl.trim() || undefined,
        imageAlt: imageAlt.trim() || undefined,
        lienUrl: lienUrl.trim() || undefined,
      });
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 backdrop-blur-md p-4"
        >
      <div onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-2xl">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              {article ? "Modifier l'article" : 'Nouvel article'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Catégorie" htmlFor="art-cat">
                <select
                  id="art-cat"
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  className={INPUT_CLS}
                >
                  <option value="">— Aucune —</option>
                  {Object.entries(CAT_INFO).map(([v, i]) => <option key={v} value={v}>{i.label}</option>)}
                </select>
              </Field>
              <Field label="Titre *" htmlFor="art-titre">
                <input
                  id="art-titre"
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                  required minLength={2} maxLength={200}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
            <Field
              label="Contenu * — markdown : **gras**, *italique*, [lien](url), - listes"
              htmlFor="art-contenu"
            >
              <textarea
                id="art-contenu"
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                required minLength={3} maxLength={50000}
                rows={8}
                className={`${TEXTAREA_CLS} font-mono`}
              />
            </Field>
            <details className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-ink transition-colors hover:bg-surface/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-lg">
                Image &amp; lien (optionnel)
              </summary>
              <div className="space-y-2 p-3 pt-2">
                <Field label="URL image (https://…)" htmlFor="art-iu">
                  <input
                    id="art-iu"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    type="url"
                    placeholder="https://i.imgur.com/exemple.jpg"
                    className={INPUT_CLS}
                  />
                </Field>
                <Field label="Texte alternatif" htmlFor="art-ia">
                  <input
                    id="art-ia"
                    value={imageAlt}
                    onChange={(e) => setImageAlt(e.target.value)}
                    maxLength={200}
                    placeholder="Description de l'image"
                    className={INPUT_CLS}
                  />
                </Field>
                <Field label="URL « lire la suite »" htmlFor="art-lu">
                  <input
                    id="art-lu"
                    value={lienUrl}
                    onChange={(e) => setLienUrl(e.target.value)}
                    type="url"
                    placeholder="https://mairie.fr/article-complet"
                    className={INPUT_CLS}
                  />
                </Field>
              </div>
            </details>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                <Save className="h-4 w-4" aria-hidden="true" />
                {submitting ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function PreviewMini({ data }: { data: GrcNewsletterDetail }) {
  return (
    <div className="max-h-[600px] overflow-y-auto rounded-r-lg border border-border bg-white text-2xs">
      <div className="bg-grc px-3 py-2 text-white">
        <div className="text-[8px] uppercase tracking-r-eyebrow opacity-80">N°{data.numero}</div>
        <div className="text-xs font-semibold">{data.titre}</div>
      </div>
      <div className="space-y-2 p-2 text-ink">
        {data.intro && (
          <div
            className="rounded border border-border p-2"
            dangerouslySetInnerHTML={{ __html: md(data.intro) }}
          />
        )}
        {data.articles.map((a) => (
          <div key={a.id} className="rounded border border-border p-2">
            {a.imageUrl && (
              <img
                src={a.imageUrl}
                alt={a.imageAlt ?? ''}
                className="mb-1 max-h-24 w-full rounded object-cover"
              />
            )}
            <div className="text-xs font-semibold">{a.titre}</div>
            <div dangerouslySetInnerHTML={{ __html: md(a.contenu) }} />
            {a.lienUrl && <a href={a.lienUrl} className="mt-1 inline-block text-grc">Lire la suite →</a>}
          </div>
        ))}
        {data.conclusion && (
          <div
            className="rounded border border-border p-2"
            dangerouslySetInnerHTML={{ __html: md(data.conclusion) }}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// DÉTAIL (preview + envoi)
// =============================================================================

export function NewsletterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();   // R13
  const [data, setData] = useState<GrcNewsletterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  async function doExportPdf() {
    if (!id) return;
    setExportingPdf(true); setError(null);
    try { await api.grc.newsletters.exportPdf(id); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Export impossible'); }
    finally { setExportingPdf(false); }
  }

  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');
  const canDelete = ['super_admin', 'admin'].includes(user?.role ?? '');

  async function reload() {
    if (!id) return;
    setLoading(true); setError(null);
    try { setData(await api.grc.newsletters.get(id)); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  async function doSend() {
    if (!data) return;
    setActionLoading(true); setError(null); setSuccess(null);
    try {
      await api.grc.newsletters.send(data.id);
      setSuccess('Newsletter envoyée — distribution en cours');
      setConfirmSend(false);
      // Polling pour voir l'état évoluer
      setTimeout(() => void reload(), 1500);
      setTimeout(() => void reload(), 5000);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur envoi');
    } finally { setActionLoading(false); }
  }
  async function doCancel() {
    if (!data) return;
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Annuler cette newsletter ?',
      description: 'La newsletter sera marquée comme annulée et ne pourra plus être envoyée.',
      confirmLabel: 'Oui, annuler',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.grc.newsletters.cancel(data.id);
      setSuccess('Newsletter annulée');
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    }
  }
  async function doDelete() {
    if (!data) return;
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Supprimer la newsletter ?',
      description: 'Cette action est irréversible.',
      confirmLabel: 'Supprimer définitivement',
      danger: true,
    });
    if (!ok) return;
    try { await api.grc.newsletters.delete(data.id); navigate('/grc/newsletters'); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }

  if (loading) return (
    <NewsLayout title="Newsletter" icon={MailOpen}>
      <Card variant="glass" padded>
        <div className="py-8 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent" role="status" />
          <p className="mt-4 text-sm text-ink-muted">Chargement…</p>
        </div>
      </Card>
    </NewsLayout>
  );
  if (error || !data) return (
    <NewsLayout title="Newsletter" icon={MailOpen}>
      <div className="space-y-3">
        <Alert variant="error" icon={AlertCircle}>{error ?? 'Newsletter introuvable'}</Alert>
        <Button variant="secondary" size="sm" onClick={() => navigate('/grc/newsletters')}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
        </Button>
      </div>
    </NewsLayout>
  );

  const st = STATUT_BADGE[data.statut];

  return (
    <NewsLayout
      title={`Newsletter N°${data.numero}`}
      icon={MailOpen}
      action={
        <div className="flex items-center gap-2">
          <Link
            to="/grc/newsletters"
            className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Retour
          </Link>
          {data.statut === 'brouillon' && canEdit && (
            <Link to={`/grc/newsletters/${data.id}/edit`}>
              <Button type="button" variant="primary" size="sm">
                <PenLine className="h-3.5 w-3.5" aria-hidden="true" /> Éditer
              </Button>
            </Link>
          )}
        </div>
      }
    >
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-3">{success}</Alert>
      )}
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* Preview complet */}
        <div className="space-y-3">
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-baseline gap-2">
              <Badge variant={st.variant} size="sm">{st.label}</Badge>
              <span className="font-mono text-xs text-ink-muted">N°{data.numero}</span>
              {data.statut === 'envoyee' && data.dateEnvoiReel && (
                <span className="ml-auto text-2xs text-ink-muted">
                  Envoyée le {new Date(data.dateEnvoiReel).toLocaleString('fr-FR')}
                </span>
              )}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-r-snug text-ink">{data.titre}</h2>
            <p className="mt-1 text-sm text-ink-muted">Objet : {data.sujetEmail}</p>
          </Card>

          {/* Aperçu full */}
          <Card variant="glass" padded={false}>
            <div className="bg-grc px-5 py-4 text-white rounded-t-r-xl">
              <div className="text-2xs uppercase tracking-r-eyebrow opacity-80">Newsletter N°{data.numero}</div>
              <h2 className="font-display text-lg font-semibold tracking-r-snug">{data.titre}</h2>
            </div>
            <div className="space-y-3 p-5 text-sm text-ink">
              {data.intro && (
                <div
                  className="rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-3 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: md(data.intro) }}
                />
              )}

              {data.articles.length === 0 ? (
                <p className="italic text-ink-muted">Aucun article dans cette newsletter.</p>
              ) : data.articles.map((a) => {
                const cat = a.categorie ? CAT_INFO[a.categorie] : null;
                return (
                  <article
                    key={a.id}
                    className="rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-3"
                  >
                    {a.imageUrl && (
                      <img
                        src={a.imageUrl}
                        alt={a.imageAlt ?? ''}
                        className="mb-2 max-h-64 w-full rounded-r-md object-cover"
                      />
                    )}
                    {cat && (
                      <div className="mb-1 inline-flex items-center gap-1 text-2xs uppercase tracking-r-eyebrow text-grc-fg">
                        <cat.Icon className="h-3 w-3" aria-hidden="true" /> {cat.label}
                      </div>
                    )}
                    <h3 className="font-display text-base font-semibold tracking-r-snug text-ink">{a.titre}</h3>
                    <div
                      className="mt-1 text-sm leading-relaxed text-ink"
                      dangerouslySetInnerHTML={{ __html: md(a.contenu) }}
                    />
                    {a.lienUrl && (
                      <p className="mt-2">
                        <a
                          href={a.lienUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-grc-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
                        >
                          Lire la suite →
                        </a>
                      </p>
                    )}
                  </article>
                );
              })}

              {data.conclusion && (
                <div
                  className="rounded-r-md border border-border bg-surface/40 backdrop-blur-glass p-3 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: md(data.conclusion) }}
                />
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar actions */}
        <aside className="space-y-3">
          {/* Archive PDF (Lot C 8.7) — disponible pour toute newsletter */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Archive</SectionEyebrow>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => void doExportPdf()}
              disabled={exportingPdf}
            >
              <FileDown className="h-4 w-4" aria-hidden="true" />
              {exportingPdf ? 'Génération…' : 'Télécharger le PDF'}
            </Button>
          </Card>

          {data.statut === 'brouillon' && canEdit && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Envoyer</SectionEyebrow>
              <Alert variant="warning" icon={AlertTriangle} className="mb-3">
                L'envoi est définitif. La newsletter sera distribuée à tous les
                citoyens abonnés (option newsletter activée dans leur compte).
              </Alert>
              {!confirmSend ? (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => setConfirmSend(true)}
                  disabled={data.articles.length === 0}
                >
                  <Send className="h-4 w-4" aria-hidden="true" /> Envoyer aux abonnés
                </Button>
              ) : (
                <>
                  <p className="text-xs font-medium text-urgent">Confirmer l'envoi ?</p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConfirmSend(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      disabled={actionLoading}
                      onClick={() => void doSend()}
                    >
                      Oui, envoyer
                    </Button>
                  </div>
                </>
              )}
              {data.articles.length === 0 && (
                <p className="mt-2 text-xs text-warning">
                  ⚠️ Au moins un article requis avant l'envoi.
                </p>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 w-full"
                onClick={() => void doCancel()}
              >
                <X className="h-4 w-4" aria-hidden="true" /> Annuler ce brouillon
              </Button>
            </Card>
          )}

          {/* Stats envoi */}
          {data.statut !== 'brouillon' && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="mb-3">Statistiques</SectionEyebrow>
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <dt className="text-ink-muted">Cibles</dt>
                  <dd className="font-medium text-ink tabular-nums">{data.cibleCount}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-muted">Envoyés</dt>
                  <dd className="font-medium text-success tabular-nums">{data.envoyeCount}</dd>
                </div>
                {data.echecCount > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-ink-muted">Échecs</dt>
                    <dd className="font-medium text-urgent tabular-nums">{data.echecCount}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-border pt-1.5">
                  <dt className="text-ink-muted">Ouvertures</dt>
                  <dd className="font-medium text-ink tabular-nums">
                    {data.ouvertCount}
                    {data.tauxOuverture !== null && (
                      <span className="ml-1 text-grc-fg">({data.tauxOuverture}%)</span>
                    )}
                  </dd>
                </div>
              </dl>
              {data.tauxOuverture !== null && (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-bg/60">
                  <div
                    className="h-full bg-grc transition-[width]"
                    style={{ width: `${data.tauxOuverture}%` }}
                    aria-hidden="true"
                  />
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-3 w-full"
                onClick={() => void reload()}
              >
                Actualiser
              </Button>
            </Card>
          )}

          {/* Métadonnées */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-2">Métadonnées</SectionEyebrow>
            <dl className="space-y-1 text-2xs">
              <div>
                <dt className="inline font-medium text-ink-muted">Créée :</dt>
                <dd className="inline text-ink"> {new Date(data.createdAt).toLocaleString('fr-FR')}</dd>
              </div>
              {data.creeParFullName && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Par :</dt>
                  <dd className="inline text-ink"> {data.creeParFullName}</dd>
                </div>
              )}
              {data.envoyeParFullName && (
                <div>
                  <dt className="inline font-medium text-ink-muted">Envoyée par :</dt>
                  <dd className="inline text-ink"> {data.envoyeParFullName}</dd>
                </div>
              )}
            </dl>
          </Card>

          {canDelete && (
            <Card variant="glass" padded>
              <Button
                type="button"
                variant="danger"
                size="sm"
                className="w-full"
                onClick={() => void doDelete()}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer définitivement
              </Button>
            </Card>
          )}
        </aside>
      </div>
      {dialogElement}{/* R13 — Dialog R2 (confirmations annulation/suppression newsletter) */}
    </NewsLayout>
  );
}
